// ============================================================================
// DOM Helpers - Common DOM manipulation utilities
// ============================================================================

import { isAeriumIcon, getAeriumIcon, renderAeriumIcon } from 'shared/icons/aerium-icons.js';

/**
 * Render an icon value as an HTML string.
 * Handles both Font Awesome classes ('fa-list' -> <i class="fas fa-list">)
 * and custom Aerium icons ('aer-afs' -> inline currentColor SVG), so every
 * surface that displays a user-picked icon renders both kinds correctly.
 * Unknown or unsafe values fall back to fa-circle.
 * @param {string} iconValue - Icon value (e.g. 'fa-list' or 'aer-afs')
 * @param {string} className - Extra classes for the icon element
 * @returns {string} HTML string
 */
export function renderIcon(iconValue, className = '') {
    const safe = typeof iconValue === 'string' && /^[a-z0-9-]+$/.test(iconValue)
        ? iconValue
        : 'fa-circle';

    const custom = getAeriumIcon(safe);
    if (custom) return renderAeriumIcon(custom, className);

    // Unknown aer-*/prem-* value (e.g. an icon later removed from the
    // registry): fall back to a neutral dot rather than a broken FA class
    if (isAeriumIcon(safe)) {
        return `<i class="fas fa-circle${className ? ` ${className}` : ''}"></i>`;
    }

    return `<i class="fas ${safe}${className ? ` ${className}` : ''}"></i>`;
}

/**
 * Escape HTML entities to prevent XSS
 * Handles all critical characters: & < > " '
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
export function escapeHtml(text) {
    if (!text) return '';
    const str = String(text);
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Parse an HTML string of table rows into detached <tr> elements. A <tbody>
 * wrapper is required: a <tr> string only parses inside table context (a <div>
 * would silently drop the rows). Returns a static array so the caller can move
 * the nodes into the live DOM without iterating a live HTMLCollection.
 * @param {string} html - One or more <tr>...</tr> strings
 * @returns {HTMLElement[]} Detached row elements
 */
export function htmlToRows(html) {
    const tbody = document.createElement('tbody');
    tbody.innerHTML = (html || '').trim();
    return Array.from(tbody.children);
}

/**
 * Snapshot a text input's value + caret so it can be restored across a DOM
 * repaint that replaces the node. Pair with restoreFieldCaret. Returns null for
 * a missing element.
 * @param {HTMLElement|null} el
 * @returns {{ value: any, selStart: number|null, selEnd: number|null }|null}
 */
export function captureFieldCaret(el) {
    if (!el) return null;
    return {
        value: el.value,
        selStart: el.selectionStart ?? null,
        selEnd: el.selectionEnd ?? null
    };
}

/**
 * Restore a value + caret captured by captureFieldCaret onto a (possibly new)
 * element. Keeps an in-progress value over a freshly painted one, then focuses
 * and restores the selection range. setSelectionRange is guarded because it
 * throws on number/email inputs.
 * @param {HTMLElement|null} el
 * @param {{ value: any, selStart: number|null, selEnd: number|null }|null} snap
 */
export function restoreFieldCaret(el, snap) {
    if (!el || !snap) return;
    if (snap.value != null && el.value !== snap.value) el.value = snap.value;
    el.focus();
    if (snap.selStart != null && el.setSelectionRange) {
        try { el.setSelectionRange(snap.selStart, snap.selEnd); } catch { /* non-text input */ }
    }
}

/**
 * Convert markdown-like syntax to safe HTML for displaying note content.
 * Escapes HTML first, then applies formatting - XSS-safe by design.
 *
 * Supported inline:  **bold**  *italic*  __underline__  ~~strike~~  `code`
 * Supported blocks:  # H1   ## H2   - bullet   1. numbered   > callout   ---
 *
 * @param {string} rawContent - Raw note content from the database
 * @returns {string} Safe HTML string with formatting applied
 */
export function formatNoteContent(rawContent) {
    if (!rawContent) return '';
    let text = escapeHtml(rawContent);

    // Inline formatting (order matters: bold before italic, underline before italic)
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/__(.+?)__/g, '<u>$1</u>');
    text = text.replace(/\*(\S(?:.*?\S)?)\*/g, '<em>$1</em>');
    text = text.replace(/~~(.+?)~~/g, '<del>$1</del>');
    text = text.replace(/`(.+?)`/g, '<code class="notes__code">$1</code>');

    // Block-level: headings (## before # so the longer marker wins)
    text = text.replace(/^## (.+)$/gm, '<span class="notes__h2">$1</span>');
    text = text.replace(/^# (.+)$/gm, '<span class="notes__h1">$1</span>');

    // Block-level: divider (--- on its own line)
    text = text.replace(/^---$/gm, '<span class="notes__divider"></span>');

    // Block-level: callout (> text on its own line)
    text = text.replace(/^&gt; (.+)$/gm, '<span class="notes__callout">$1</span>');

    // Block-level: numbered lines (1. text) - the literal number is kept visible
    text = text.replace(/^(\d+)\. (.+)$/gm, '<span class="notes__ordered"><span class="notes__ordered-num">$1.</span> $2</span>');

    // Block-level: bullet lines (- text at start of line)
    text = text.replace(/^- (.+)$/gm, '<span class="notes__bullet">$1</span>');

    // Inline images: ![alt](storage:<path>). Done LAST so the generated <img>
    // markup (whose class "notes__image" contains a "__") is never re-processed
    // by the inline underline pass above. We emit only the private storage path
    // in a data attribute (no src); hydrateRichTextImages() mints a short-lived
    // signed URL at render time. The "storage:" prefix means a crafted string can
    // never inject an arbitrary http/javascript/data source.
    text = text.replace(
        /!\[([^\]]*)\]\(storage:([^)\s]+)\)/g,
        '<img class="notes__image" data-rt-path="$2" alt="$1" loading="lazy">'
    );

    return text;
}

/**
 * Extract initials from a name
 * @param {string} name - Full name to extract initials from
 * @returns {string} Initials (1-2 characters, uppercase)
 */
export function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Generate a deterministic color from a name for avatars.
 * Returns a theme variable reference (not a hardcoded hex) so avatars stay
 * theme-aware across Light / Dark / Sage.
 * @param {string} name - Name to generate color from
 * @returns {string} CSS variable reference, e.g. 'var(--color-primary)'
 */
export function getAvatarColor(name) {
    const colors = [
        'var(--color-primary)', 'var(--color-danger)', 'var(--color-success)', 'var(--color-warning)',
        'var(--color-purple)', 'var(--color-pink)', 'var(--color-teal)', 'var(--color-amber)'
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
}

/**
 * Render an avatar element (img or initials circle) as an HTML string.
 * Consolidates the repeated getInitials + getAvatarColor + conditional
 * img/div pattern used throughout the app.
 *
 * @param {Object|null} profile - Profile-like object with optional fields:
 *   avatar_url, full_name, short_name, icon
 * @param {Object} [options] - Rendering options
 * @param {string}  options.className   - Base BEM class (e.g. "tasks__assignee-avatar").
 *                                        The img variant appends "--img" automatically.
 * @param {string}  [options.name]      - Override display name (skips profile lookup)
 * @param {string}  [options.icon]      - Override glyph (skips profile lookup)
 * @param {string}  [options.colorKey]  - Key passed to getAvatarColor instead of name
 *                                        (e.g. user_id for consistent color per user)
 * @param {string}  [options.title]     - Tooltip text for the element
 * @param {string}  [options.extraStyle]- Additional inline style string applied
 *                                        to both the img and the initials variant
 *                                        (e.g. "z-index: 2; --avatar-color: ...")
 * @returns {string} HTML string for the avatar element
 */
export function renderAvatar(profile, options = {}) {
    const {
        className = '',
        name: nameOverride,
        icon: iconOverride,
        colorKey,
        title,
        extraStyle
    } = options;

    const displayName = nameOverride
        || profile?.full_name
        || profile?.short_name
        || 'Unknown';

    const titleAttr = title != null
        ? ` title="${escapeHtml(title)}"`
        : '';

    // If profile has an avatar image, render an <img>
    if (profile?.avatar_url) {
        const styleAttr = extraStyle ? ` style="${extraStyle}"` : '';
        return `<img src="${escapeHtml(profile.avatar_url)}" alt=""${titleAttr} class="${className} ${className}--img"${styleAttr}>`;
    }

    const color = getAvatarColor(colorKey || displayName);

    let style = `background: ${color};`;
    if (extraStyle) style += ` ${extraStyle}`;

    // A non-person entry (the "Tray" option on a profile_plus_options field,
    // say) has no face to show, so it carries a glyph instead of initials -
    // painted in the same circle so it lines up with the avatars around it.
    // Real profile rows never carry `icon`, so this is inert for staff.
    const glyph = iconOverride || profile?.icon;
    if (glyph) {
        return `<div class="${className}"${titleAttr} style="${style}">${renderIcon(glyph)}</div>`;
    }

    // Otherwise render an initials circle
    return `<div class="${className}"${titleAttr} style="${style}">${getInitials(displayName)}</div>`;
}

/**
 * Render a standard empty-state block. Pairs with the shared `.empty-state`
 * CSS classes in main.css. Use inside panels, tabs, and modals wherever a
 * "no data yet" placeholder is needed.
 *
 * @param {Object} options
 * @param {string} [options.icon='fas fa-inbox'] - Font Awesome icon class
 * @param {string} options.title - Heading text
 * @param {string} [options.text] - Optional description
 * @param {string} [options.actionHtml] - Optional HTML for a CTA (e.g. a button)
 * @param {boolean} [options.compact=false] - Use the compact variant
 * @returns {string} HTML string
 */
export function renderEmptyState({ icon = 'fas fa-inbox', title, text, actionHtml, compact = false } = {}) {
    const wrapperClass = compact ? 'empty-state empty-state--compact' : 'empty-state';
    const safeTitle = escapeHtml(title || '');
    const safeText = text ? `<p class="empty-state__text">${escapeHtml(text)}</p>` : '';
    const action = actionHtml ? `<div class="empty-state__action">${actionHtml}</div>` : '';

    return `
        <div class="${wrapperClass}">
            <i class="empty-state__icon ${icon}"></i>
            <h4 class="empty-state__title">${safeTitle}</h4>
            ${safeText}
            ${action}
        </div>
    `;
}
